/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getCatalogId, ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core-internal';
import {
    CommerceProperty,
    ProductCatalog,
    ProductDeliveryOptions,
    ProductDimensionValue,
    ProductDimensionValueInventoryAvailability,
    ProductPrice,
    ReleasedProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { BulkPurchase } from '@msdyn365-commerce-modules/bulk-purchase';
import {
    ArrayExtensions,
    DeliveryMode,
    FinitePromiseQueue,
    FinitePromiseQueueError,
    getDeliveryOptionsForSelectedVariant,
    GetDeliveryOptionsForSelectedVariantInput,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getInventoryLevelCodeFromDimensionValue,
    getPriceForSelectedVariant,
    getProductAvailabilitiesForSelectedVariant,
    getSelectedVariant,
    IDimensionsApp,
    IDimensionValueForSelectedVariant,
    InventoryLevelValues,
    IProductInventoryInformation,
    IPromiseQueue,
    PriceForSelectedVariantInput,
    ProductAvailabilitiesForSelectedVariantInput,
    SelectedVariantInput,
    setDimensionValuesToQuery
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    updateMaxQuantityForCartLineItem
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    getBuyBoxInventoryLabel,
    getBuyboxKeyInPrice,
    getBuyboxProductAddToOrderTemplate,
    getBuyboxProductAddToWishlist,
    getBuyboxProductComparisonButton,
    getBuyboxProductDescription,
    getBuyboxProductPrice,
    getBuyboxProductQuantity,
    getBuyboxProductRating,
    getBuyboxProductTitle,
    getBuyboxProductUnitOfMeasure,
    getBuyboxShopSimilarButton,
    getQuantityLimitsMessages,
    IBuyboxProductConfigureDropdownViewProps,
    RetailDefaultOrderQuantityLimitsFeatureName
} from '@msdyn365-commerce-modules/buybox';
import { getBuyboxProductConfigure } from '@msdyn365-commerce-modules/buybox';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCommonData,
    IBuyboxExtentedProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IErrorState,
    ShopSimiliarButtonType
} from '@msdyn365-commerce-modules/buybox';
import { IBuyboxData } from './buybox.data';
import { IBuyboxProps, IBuyboxResources } from './buybox.props.autogenerated';
import { getBuyboxFindInStore, IBuyboxFindInStoreViewProps } from './components/buybox-find-in-store';
import { RecentlyViewedState } from '@msdyn365-commerce-modules/buybox';
import { Dropdown, DropdownWithSearch, IDropdownItemProps, IDropdownOnSelectionChangeNotification } from '../../components/dropdown';

import { IBuyboxCallbacks } from '../../components/AddToCart/buyboxInterface';
import { getBuyboxAddToCart } from '../../components/AddToCart/buybox-components';
import { getEnquiryProductEmailData, getTimeSlotsAsync } from '../../proxy/DataActionExtension.g';
import { IProductFringe, IProductTimeSlot, ITimeSlot } from '../../proxy/DataServiceEntities.g';
import { htmltoText } from '../../utils/text-utils';
import { isFreePrice } from '../../utils/price-utils';
import { getProductSchedulingMode } from '../../utils/product-selection-mode';
import {
    getFringesRequestParams,
    IFringesRequestParams,
    loadProductFringes,
    getEmptySchedulingState,
    buildEventsLoadedState,
    buildFringesLoadedState
} from '../../utils/product-scheduling-helpers';

export interface IBuyboxViewProps extends IBuyboxProps<IBuyboxData> {
    state: IBuyboxState;
    ModuleProps: IModuleProps;
    ProductInfoContainerProps: INodeProps;
    MediaGalleryContainerProps: INodeProps;
    callbacks: IBuyboxCallbacks;
    mediaGallery?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    rating?: React.ReactNode;
    price?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    bulkPurchaseLink?: React.ReactNode;
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps;
    addToWishlist?: IBuyboxAddToWishlistViewProps;
    max: number | undefined;
    addToCart?: IBuyboxAddToCartViewProps;
    findInStore?: IBuyboxFindInStoreViewProps;
    quantity?: IBuyboxProductQuantityViewProps;
    configure?: IBuyboxProductConfigureViewProps;
    inventoryLabel?: React.ReactNode;
    shopSimilarLook?: IBuyboxShopSimilarLookViewProps;
    shopSimilarDescription?: IBuyboxShopSimilarLookViewProps;
    quantityLimitsMessages: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    keyInPrice?: IBuyboxKeyInPriceViewProps;
    unitOfMeasure?: React.ReactNode;
    catalogs?: ProductCatalog[] | undefined;
    eventsConfigureDropdown?: IBuyboxProductConfigureDropdownViewProps;
    timeSlotsConfigureDropdown?: IBuyboxProductConfigureDropdownViewProps;
    fringeConfigureDropdown?: IBuyboxProductConfigureDropdownViewProps; // NEC extension
    fringeDescription?: React.ReactNode; // NEC extension
    enquireProductButton?: React.ReactNode;
}

interface IExtendedBuyboxState extends IBuyboxState {
    availableEvents?: IProductTimeSlot[];
    selectedEvent?: IProductTimeSlot;
    selectedTimeSlot?: ITimeSlot;
    eventErrorMessage?: string;
    timeSlotErrorMessage?: string;
    availableFringes?: IProductFringe[]; // NEC extension
    selectedFringe?: IProductFringe; // NEC extension
    fringeErrorMessage?: string; // NEC extension
}

/**
 * Buybox Module.
 */
class Buybox extends React.PureComponent<IBuyboxProps<IBuyboxData>, IExtendedBuyboxState> {
    /**
     * A queue of tasks of processing the changes in the dimensions.
     * Limit to two processes:
     * 1 - for the current process, which is under execution at the moment.
     * 2 - next process, which will process the latest version of data.
     * @remark Enqueueing new promises will discard the previous ones (except the one which is under processing).
     */
    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private dimensions: { [id: number]: string } = {};

    private readonly buyboxCallbacks: IBuyboxCallbacks = {
        updateQuantity: (newQuantity: number): boolean => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;

            this.setState({ quantity: newQuantity, errorState });
            return true;
        },
        updateErrorState: (newErrorState: IErrorState): void => {
            this.setState({ errorState: newErrorState });
        },
        updateSelectedProduct: (
            newSelectedProduct: Promise<SimpleProduct | null>,
            newInventory: IProductInventoryInformation | undefined,
            newPrice: ProductPrice | undefined,
            newDeliveryOptions: ProductDeliveryOptions | undefined
        ): void => {
            this.setState({
                selectedProduct: newSelectedProduct,
                productAvailableQuantity: newInventory,
                productDeliveryOptions: newDeliveryOptions
            });
            this._updatePrice(newPrice);
        },
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this.dimensions[selectedDimensionId] = selectedDimensionValueId;
            return this.dimensionUpdateQueue
                .enqueue(async () => {
                    return this._updateDimensions();
                })
                .catch((error: unknown) => {
                    // Ignore discarded processes.
                    if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                        throw error;
                    }
                });
        },
        getDropdownName: (dimensionType: number, resources: IBuyboxResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeModalOpen: (isModalOpen: boolean): void => {
            this.setState({ modalOpen: isModalOpen });
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        },

        /**
         * Update isUpdatingDeliveryOptions state.
         *
         * @param isUpdatingDeliveryOptions - The status of updating delivery options.
         */
        changeUpdatingDeliveryOptions: (isUpdatingDeliveryOptions: boolean): void => {
            this.setState({ isUpdatingDeliveryOptions });
        },

        updateKeyInPrice: (customPrice: number): void => {
            // Remove custom amount error when updating the custom price
            const errorState = { ...this.state.errorState };
            errorState.customAmountError = undefined;

            this.setState({ isPriceKeyedIn: true, keyInPriceAmount: customPrice, errorState });
            this._updatePrice(this.state.productPrice, customPrice);
        },

        setTimeSlotError: () => {
            // NEC extension start
            const mode = getProductSchedulingMode(this.props.context);

            if (mode === 'fringes') {
                this.setState({
                    fringeErrorMessage: !this.state.selectedFringe ? this.props.resources.fringeErrorMessage : undefined,
                    eventErrorMessage: undefined,
                    timeSlotErrorMessage: undefined
                });
                return;
            }

            if (mode === 'hallTimeslots') {
                this.setState({
                    timeSlotErrorMessage: !this.state.selectedTimeSlot ? this.props.resources.timeSlotErrorMessage : undefined,
                    eventErrorMessage: !this.state.selectedEvent ? this.props.resources.eventErrorMessage : undefined,
                    fringeErrorMessage: undefined
                });
                return;
            }

            this.setState({
                fringeErrorMessage: undefined,
                eventErrorMessage: undefined,
                timeSlotErrorMessage: undefined
            });
        }
        // NEC extension end
    };

    private readonly telemetryContent: ITelemetryContent;

    public constructor(props: IBuyboxProps<IBuyboxData>, state: IBuyboxState) {
        super(props);
        this.state = {
            errorState: {
                configureErrors: {}
            },
            quantity: 1,
            min: undefined,
            max: undefined,
            selectedProduct: undefined,
            productPrice: undefined,
            productDeliveryOptions: undefined,
            availableEvents: undefined,
            selectedEvent: undefined,
            timeSlotErrorMessage: undefined,
            availableFringes: undefined, // NEC extension
            selectedFringe: undefined, // NEC extension
            fringeErrorMessage: undefined, // NEC extension
            eventErrorMessage: undefined,

            modalOpen: false,
            isUpdatingDimension: false,
            isUpdatingDeliveryOptions: false
        };
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }

    public async componentDidMount(): Promise<void> {
        const {
            data: {
                product: { result: product },
                productPrice
            },
            context
        } = this.props;

        productPrice.then(async result => {
            this._updatePrice(result);
        });

        if (product) {
            // Check if the product is service or not by product type
            if (product.ItemTypeValue === ReleasedProductType.Service) {
                this.setState({ isServiceItem: true });
            }
            await this._updateQuantitiesInState(product);
        }
        const mode = getProductSchedulingMode(this.props.context); // NEC extension

        if (product?.ItemId) {
            //let catalogId = 5637162576;
            const catalogId = getCatalogId(this.props.context.request);

            // NEC extension start
            if (catalogId === 0 || mode === 'none') {
                this.setState(getEmptySchedulingState());
            } else if (mode === 'fringes') {
                const params = getFringesRequestParams(product.ItemId, this.props.context);
                await this._getProductFringes(params);
                // NEC extension end
            } else {
                await this._getProductEvents(product.ItemId, catalogId);
            }
        }

        if (product && context.app.config.maxRecentlyViewedItemsCount > 0) {
            RecentlyViewedState.instance(this.props.context, context.app.config.maxRecentlyViewedItemsCount, product.RecordId);
        }
    }

    public render(): JSX.Element | null {
        const {
            slots: { mediaGallery, productComparisonButton },
            data: {
                product: { result: product }
            },
            config: { className = '' }
        } = this.props;

        const { max } = this.state;

        if (!product) {
            this.props.context.telemetry.error('Product content is empty, module wont render');
            return null;
        }

        const props = this.props as IBuyboxExtentedProps<IBuyboxCommonData>;
        const defaultMinimumKeyInPrice = 10;
        const defaultMaximumKeyInPrice = 100;

        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const inventoryLevel = context.app.config.inventoryLevel;
        const dimensionValuesWithInventory = ArrayExtensions.flatten(
            ArrayExtensions.validValues(this.props.data.productDimensions.result?.map(value => value.dimensionValuesWithInventory))
        );
        const hasAvailableProducts =
            !ArrayExtensions.hasElements(dimensionValuesWithInventory) ||
            dimensionValuesWithInventory.some((value: unknown) => {
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(
                    value as ProductDimensionValueInventoryAvailability,
                    inventoryLevel
                );
                return inventoryLevelCode !== InventoryLevelValues.outOfStock;
            });
        const catalogId = getCatalogId(this.props.context.request);
        const catalogs = this.props.data.catalogs?.result?.filter(item => item.RecordId === catalogId);

        const useEnquiryProductFlow =
            this.props?.context?.app?.config?.enableEnquireProductFlow &&
            this.state?.productPrice &&
            isFreePrice(this.state?.productPrice!);

        const mode = getProductSchedulingMode(this.props.context); // NEC extension

        const shouldRenderHallTimeslots = !useEnquiryProductFlow && mode === 'hallTimeslots'; // NEC extension
        const shouldRenderFringes = !useEnquiryProductFlow && mode === 'fringes'; // NEC extension
        const shouldRenderScheduling = !useEnquiryProductFlow && mode !== 'none'; // NEC extension

        const viewProps: IBuyboxViewProps = {
            ...(this.props as IBuyboxProps<IBuyboxData>),
            state: this.state,
            mediaGallery: mediaGallery && mediaGallery.length > 0 ? mediaGallery[0] : undefined,
            ModuleProps: {
                moduleProps: this.props,
                className: classnames('ms-buybox', className)
            },
            ProductInfoContainerProps: {
                className: 'ms-buybox__content'
            },
            MediaGalleryContainerProps: {
                className: 'ms-buybox__media-gallery'
            },
            telemetryContent: this.telemetryContent,
            callbacks: this.buyboxCallbacks,
            title: getBuyboxProductTitle(props),
            description: getBuyboxProductDescription(props),
            bulkPurchaseLink: this._renderBuyboxBulkPurchaseLink(),
            unitOfMeasure: !useEnquiryProductFlow ? getBuyboxProductUnitOfMeasure(props) : undefined,
            configure: getBuyboxProductConfigure(props, this.state, this.buyboxCallbacks),
            findInStore: getBuyboxFindInStore(this.props, this.state, this.buyboxCallbacks),
            price: !useEnquiryProductFlow ? getBuyboxProductPrice(props, this.state) : undefined,
            addToCart: !useEnquiryProductFlow
                ? getBuyboxAddToCart(
                      props,
                      this.state,
                      this.buyboxCallbacks,
                      defaultMinimumKeyInPrice,
                      defaultMaximumKeyInPrice,
                      undefined,
                      hasAvailableProducts
                  )
                : undefined,
            productComparisonButton: ArrayExtensions.hasElements(productComparisonButton)
                ? getBuyboxProductComparisonButton(
                      productComparisonButton[0],
                      product,
                      getCatalogId(this.props.context.request),
                      this.state
                  )
                : undefined,
            addToOrderTemplate: getBuyboxProductAddToOrderTemplate(props, this.state, this.buyboxCallbacks),
            addToWishlist: getBuyboxProductAddToWishlist(props, this.state, this.buyboxCallbacks),
            rating: !props.context.app.config.hideRating && getBuyboxProductRating(props),
            quantity:
                product.IsGiftCard || useEnquiryProductFlow ? undefined : getBuyboxProductQuantity(props, this.state, this.buyboxCallbacks),
            inventoryLabel: getBuyBoxInventoryLabel(props),
            shopSimilarLook:
                this.props.config.enableShopSimilarLooks && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Looks)
                    : undefined,
            shopSimilarDescription:
                this.props.config.enableShopSimilarDescription && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Description)
                    : undefined,
            keyInPrice:
                this.props.config.enableKeyInPrice && this.state.isCustomPriceSelected
                    ? getBuyboxKeyInPrice(props, this.state, this.buyboxCallbacks)
                    : undefined,
            quantityLimitsMessages: getQuantityLimitsMessages(props, this.state),
            max,
            // NEC extension start
            catalogs: shouldRenderScheduling ? catalogs : undefined,
            eventsConfigureDropdown: shouldRenderHallTimeslots ? this._getEventsConfigureDropdown() : undefined,
            timeSlotsConfigureDropdown: shouldRenderHallTimeslots ? this._getTimeSlotsConfigureDropdown() : undefined,
            fringeConfigureDropdown: shouldRenderFringes ? this._getFringeConfigureDropdown() : undefined,
            fringeDescription:
                shouldRenderFringes && this.state.selectedFringe ? (
                    <div className='ms-buybox__fringe-description'>{this.state.selectedFringe.Description ?? ''}</div>
                ) : (
                    undefined
                ),
            // NEC extension end
            enquireProductButton: useEnquiryProductFlow ? this._renderEnqureProductButton() : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    //NEC extension - start
    private _getProductEvents = async (itemId: string, catalogId: number) => {
        try {
            const result = await getTimeSlotsAsync({ callerContext: this.props.context.actionContext }, itemId, catalogId);
            const availableEvents = result?.length ? result : undefined;
            this.setState(buildEventsLoadedState(availableEvents));
        } catch (e) {
            this.setState(getEmptySchedulingState());
        }
    };

    private readonly _mapEventsToDropdownList = (timeSlots?: IProductTimeSlot[]): IDropdownItemProps[] | undefined =>
        timeSlots?.map((timeSlot, i, arr) => ({
            value: timeSlot.SlotName ?? '',
            id: String(timeSlot.RecId),
            isDisabled: false,
            isDefault: arr.length === 1
        }));

    private readonly _onEventSelectionChange = async (notification: IDropdownOnSelectionChangeNotification) => {
        const sourceEvents = this.state.availableEvents;
        const selectedEvent = sourceEvents?.find(event => event.RecId.toString() === notification.selectId);

        if (selectedEvent) {
            this.setState({
                selectedEvent,
                timeSlotErrorMessage: undefined,
                eventErrorMessage: undefined,
                selectedTimeSlot: undefined
            });
        }
    };

    private readonly _getEventsConfigureDropdown = (): IBuyboxProductConfigureDropdownViewProps | undefined => {
        const sourceEvents = this.state.availableEvents;
        const dropdownList = this._mapEventsToDropdownList(sourceEvents);

        if (!dropdownList) {
            return;
        }

        const dropdownToggleName = sourceEvents?.length
            ? this.props.resources.eventToggleName
            : this.props.resources.noEventAvailableDropdownToggleName;

        return {
            ContainerProps: {
                className: 'ms-buybox__dropdown'
            },
            LabelContainerProps: {
                tag: 'label',
                className: 'ms-buybox__dropdown-quantity-label',
                htmlFor: 'ms-buybox__dropown-quantity-input-time-slots-id'
            },
            errors: this.state.eventErrorMessage && (
                <span className='msc-alert msc-alert-noborder msc-alert-danger' role='alert' aria-live='assertive'>
                    <span className='msi-exclamation-triangle' aria-hidden='true' />
                    <span>{this.state.eventErrorMessage}</span>
                </span>
            ),
            heading: <div>{this.props.resources.eventDropdownHeading}</div>,
            select: (
                <Dropdown
                    dropdownId='ms-buybox__dropown-quantity-input-time-slots-id'
                    dropdownName={this.props.resources.eventDropdownName}
                    dropdownToggleName={dropdownToggleName}
                    dropdownList={dropdownList}
                    onChange={this._onEventSelectionChange}
                    selectedId={this.state?.selectedEvent?.RecId?.toString() ?? ''}
                />
            )
        };
    };

    private _getProductFringes = async (params: IFringesRequestParams) => {
        try {
            const result = await loadProductFringes({ callerContext: this.props.context.actionContext }, params);

            const availableFringes = result?.length ? result : undefined;

            this.setState(buildFringesLoadedState(availableFringes));
        } catch (e) {
            this.setState(getEmptySchedulingState());
        }
    };

    private readonly _mapFringesToDropdownList = (fringes?: IProductFringe[]): IDropdownItemProps[] | undefined =>
        fringes?.map((fringe, i, arr) => ({
            value: fringe.Description ?? `Fringe ${fringe.Id}`,
            id: fringe.Id,
            isDisabled: false,
            isDefault: arr.length === 1
        }));

    private readonly _onFringeSelectionChange = async (notification: IDropdownOnSelectionChangeNotification) => {
        const sourceFringes = this.state.availableFringes;
        const selectedFringe = sourceFringes?.find(fringe => fringe.Id === notification.selectId);

        if (selectedFringe) {
            this.setState({ selectedFringe, fringeErrorMessage: undefined });
        }
    };

    private readonly _getFringeConfigureDropdown = (): IBuyboxProductConfigureDropdownViewProps | undefined => {
        const sourceFringes = this.state.availableFringes;
        const dropdownList = this._mapFringesToDropdownList(sourceFringes);

        if (!dropdownList) {
            return;
        }

        const dropdownToggleName = sourceFringes?.length
            ? this.props.resources.fringesToggleName
            : this.props.resources.noFringesAvailableDropdownToggleName;

        return {
            ContainerProps: {
                className: 'ms-buybox__dropdown'
            },
            LabelContainerProps: {
                tag: 'label',
                className: 'ms-buybox__dropdown-quantity-label ms-buybox__dropdown-fringe-label',
                htmlFor: 'ms-buybox__dropown-quantity-input-fringes-id'
            },
            heading: <div>{this.props.resources.fringeDropdownHeading} </div>,
            errors: this.state.fringeErrorMessage && (
                <span className='msc-alert msc-alert-noborder msc-alert-danger' role='alert' aria-live='assertive'>
                    <span className='msi-exclamation-triangle' aria-hidden='true' />
                    <span>{this.state.fringeErrorMessage}</span>
                </span>
            ),
            select: (
                <DropdownWithSearch
                    dropdownId='ms-buybox__dropown-quantity-input-fringes-id'
                    dropdownName={this.props.resources.fringeDropdownName}
                    dropdownToggleName={dropdownToggleName}
                    dropdownList={dropdownList}
                    onChange={this._onFringeSelectionChange}
                    selectedId={this.state.selectedFringe?.Id ?? ''}
                />
            )
        };
    };

    private readonly _mapTimeSlotsToDropdownList = (timeSlots?: ITimeSlot[]): IDropdownItemProps[] | undefined => {
        const slots = timeSlots?.map((timeSlot, i, arr) => ({
            value: timeSlot.Name ?? '',
            id: `${timeSlot?.ValueFrom}~${timeSlot?.ValueTo}`,
            isDisabled: false,
            isDefault: arr.length === 1
        }));

        return slots;
    };

    private readonly _onTimeSlotSelectionChange = async (notification: IDropdownOnSelectionChangeNotification) => {
        const sourceTimeSlots = this.state.selectedEvent?.TimeSlots;
        const selectedTimeSlot = sourceTimeSlots?.find(timeSlot => `${timeSlot?.ValueFrom}~${timeSlot?.ValueTo}` === notification.selectId);

        if (selectedTimeSlot) {
            this.setState({ selectedTimeSlot: selectedTimeSlot, timeSlotErrorMessage: undefined });
        }
    };

    private readonly _getTimeSlotsConfigureDropdown = (): IBuyboxProductConfigureDropdownViewProps | undefined => {
        const sourceTimeSlots = this.state.selectedEvent?.TimeSlots;
        const dropdownList = this._mapTimeSlotsToDropdownList(sourceTimeSlots);

        if (!dropdownList) {
            return;
        }

        const dropdownToggleName = dropdownList?.length
            ? this.props.resources.timeSlotDropdownToggleName
            : this.props.resources.noTimeSlotsAvailableDropdownToggleName;

        return {
            ContainerProps: {
                className: 'ms-buybox__dropdown'
            },
            LabelContainerProps: {
                tag: 'label',
                className: 'ms-buybox__dropdown-quantity-label',
                htmlFor: 'ms-buybox__dropown-quantity-input-time-slots-id'
            },
            errors: this.state.timeSlotErrorMessage && (
                <span className='msc-alert msc-alert-noborder msc-alert-danger' role='alert' aria-live='assertive'>
                    <span className='msi-exclamation-triangle' aria-hidden='true' />
                    <span>{this.state.timeSlotErrorMessage}</span>
                </span>
            ),
            heading: <div>{this.props.resources.timeSlotDropdownHeading}</div>,
            select: (
                <Dropdown
                    dropdownId='ms-buybox__dropown-quantity-input-time-slots-id-2'
                    dropdownName={this.props.resources.timeSlotDropdownName}
                    dropdownToggleName={dropdownToggleName}
                    dropdownList={dropdownList}
                    onChange={this._onTimeSlotSelectionChange}
                    selectedId={
                        this.state?.selectedTimeSlot
                            ? `${this.state?.selectedTimeSlot?.ValueFrom}~${this.state?.selectedTimeSlot?.ValueTo}`
                            : ''
                    }
                />
            )
        };
    };

    private getEnailPlaceholders = async () => {
        const catalogId = getCatalogId(this.props.context.request);
        const catalog = this.props.data.catalogs?.result?.find(item => item.RecordId === catalogId);
        const catalogName = catalog?.Name ?? '';

        const product = this.props.data.product?.result;
        const productName = product?.Name ?? '';
        const itemId = product?.ItemId ?? '';

        return [
            { key: '{catalogName}', value: catalogName },
            { key: '{productName}', value: productName },
            { key: '{itemId}', value: itemId }
        ];
    };

    private getEmailContent = async () => {
        const { enquireProductRecipient, enquireProductEmailSubject, enquireProductEmailBody } = this.props?.context?.app?.config || {};

        const catalogId = getCatalogId(this.props.context.request);
        const productId = this.props.data.product?.result?.RecordId ?? 0;

        let emailData;
        try {
            emailData = await getEnquiryProductEmailData({ callerContext: this.props.context.actionContext }, productId, catalogId);
        } catch (e) {
            emailData = null;
        }

        let body = emailData?.Content?.length ? emailData.Content : (enquireProductEmailBody as string) ?? '';
        let recipient = emailData?.Recipient?.length ? emailData.Recipient : enquireProductRecipient ?? '';

        let bodyText = htmltoText(body ?? '');
        let recipientText = encodeURIComponent(recipient ?? '');
        let subjectText = enquireProductEmailSubject ?? '';

        return { bodyText, recipientText, subjectText };
    };

    private openEmailApp = async () => {
        let { bodyText, recipientText, subjectText } = await this.getEmailContent();

        const placeholders = await this.getEnailPlaceholders();
        placeholders.forEach(placeholder => {
            subjectText = subjectText.replace(placeholder.key, placeholder.value);
            bodyText = bodyText.replace(placeholder.key, placeholder.value);
        });

        const encodedSubject = encodeURIComponent(subjectText);
        const encodedBody = encodeURIComponent(bodyText);

        const mailTo = `mailto:${recipientText}?subject=${encodedSubject}&body=${encodedBody}`;

        window.location.href = mailTo;
    };

    private _renderEnqureProductButton(): React.ReactNode {
        if (this.props?.context?.app?.config?.enableEnquireProductFlow === true) {
            return (
                <Button
                    className='msc-add-to-cart nec-buybox__enquire-product'
                    title={this.props.resources.enquiryProduct}
                    onClick={this.openEmailApp}
                >
                    <span className='nec-buybox__enquire-product_icon' aria-hidden='true'>
                        <svg width='18' height='18' viewBox='0 0 20 20' focusable='false'>
                            <circle cx='10' cy='10' r='9' fill='none' stroke='currentColor' strokeWidth='2' />
                            <path d='M10 6v8M6 10h8' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                        </svg>
                    </span>
                    <span className='nec-buybox__enquire-product_label' aria-hidden='true'>
                        {this.props.resources.enquiryProduct}
                    </span>
                </Button>
            );
        }

        return null;
    }
    //NEC extension - end

    /**
     * Handles anchor click event and scrolls to the given bulk purchase module.
     * @param bulkPurchaseModule - Bulk purchase module.
     * @returns Handler.
     */
    private readonly _onBulkPurchaseLinkClick = (bulkPurchaseModule: Element) => () => {
        const boundingRect = bulkPurchaseModule.getBoundingClientRect();
        window.scrollTo(boundingRect.left, boundingRect.top);
    };

    private _renderBuyboxBulkPurchaseLink(): React.ReactNode {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Temporary switch for the feature which is under development.
        if (!this.props.context.app.config.isBulkPurchaseEnabled) {
            return null;
        }

        if (!msdyn365Commerce.isBrowser) {
            return null;
        }

        const bulkPurchaseModules = Array.from(document.getElementsByClassName(BulkPurchase.className));

        if (!ArrayExtensions.hasElements(bulkPurchaseModules)) {
            return null;
        }

        return (
            <Button
                className='ms-buybox__bulk-purchase-anchor'
                title={this.props.resources.bulkPurchaseLinkText}
                onClick={this._onBulkPurchaseLinkClick(bulkPurchaseModules[0])}
            >
                {this.props.resources.bulkPurchaseLinkText}
            </Button>
        );
    }

    private _updatePrice(newPrice: ProductPrice | undefined, customPrice: number | undefined = this.state.keyInPriceAmount): void {
        if (this.state.isCustomPriceSelected && newPrice) {
            newPrice.CustomerContextualPrice = customPrice;
        }
        this.setState({ productPrice: newPrice });
    }

    private _getProductVariant(dimensionsToUpdate: { [id: number]: string }) {
        const {
            data: {
                product: { result: product }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const productDimensions = this.props.data.productDimensions.result!;

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions
            .map(dimension => {
                const dimensions = (dimension.dimensionValuesWithInventory ??
                    dimension.DimensionValues ??
                    []) as IDimensionValueForSelectedVariant[];
                return {
                    DimensionTypeValue: dimension.DimensionTypeValue,
                    DimensionValue:
                        dimensions.find(
                            value => value.DimensionValue?.RecordId.toString() === dimensionsToUpdate[dimension.DimensionTypeValue]
                        )?.DimensionValue ?? dimension.DimensionValue,
                    ExtensionProperties: dimension.ExtensionProperties
                };
            })
            .filter(dimension => dimension.DimensionValue);
        setDimensionValuesToQuery(this.props.context.actionContext.requestContext.url.requestUrl, mappedDimensions);

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProductPromise = getSelectedVariant(
            new SelectedVariantInput(
                product!.MasterProductId ? product!.MasterProductId : product!.RecordId,
                channelId,
                undefined,
                undefined,
                this.props.context.request
            ),
            actionContext
        );
        this.setState({ selectedProduct: selectedProductPromise });

        return { productVariantPromise: selectedProductPromise, mappedDimensions };
    }

    private readonly _updateDimensions = async (): Promise<void> => {
        const product = this.props.data.product.result;

        const productDimensions = this.props.data.productDimensions.result;

        const hasProductDimensions = ArrayExtensions.hasElements(productDimensions);

        if (!product || !hasProductDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({ isUpdatingDimension: true, isUpdatingDeliveryOptions: true });

        // Step 1: Clear error state to display relevant errors
        if (this.state.errorState.otherError || this.state.errorState.quantityError) {
            const clearErrorState = { ...this.state.errorState };
            clearErrorState.otherError = undefined;
            if (this.state.errorState.errorHost === 'ADDTOCART') {
                clearErrorState.quantityError = undefined;
                clearErrorState.errorHost = undefined;
            }
            this.setState({ errorState: clearErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            if (this.state.errorState.configureErrors[key]) {
                this.setState(previousState => {
                    const errorState = previousState.errorState;
                    errorState.configureErrors[key] = undefined;
                    return { errorState };
                });
            }
        }

        const data = this._getProductVariant(dimensionsToUpdate);
        const productVariant: SimpleProduct | null = await data.productVariantPromise;

        if (!productVariant) {
            return;
        }

        const promises: Promise<void>[] = [
            this._getDimensionsForSelectedVariant(productVariant, data.mappedDimensions),
            this._hydrateInventoryInfo(productVariant),
            this._hydratePrice(productVariant),
            this._hydrateDeliveryOptions(productVariant),
            this._updateQuantitiesInState(productVariant)
        ];

        await Promise.all(promises);
    };

    private async _getDimensionsForSelectedVariant(
        productVariant: SimpleProduct,
        mappedDimensions: {
            /* eslint-disable @typescript-eslint/naming-convention -- Retail proxy values */
            DimensionTypeValue: number;
            DimensionValue: ProductDimensionValue | undefined;
            ExtensionProperties: CommerceProperty[] | undefined;
            /* eslint-enable @typescript-eslint/naming-convention -- Retail proxy values */
        }[]
    ): Promise<void> {
        await getDimensionsForSelectedVariant(
            new GetDimensionsForSelectedVariantInput(
                productVariant.MasterProductId ? productVariant.MasterProductId : productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                mappedDimensions,
                this.props.context.request
            ),
            this.props.context.actionContext
        );
    }

    private async _hydrateInventoryInfo(productVariant: SimpleProduct): Promise<void> {
        // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
        // so that places like add to cart can await it
        const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
            new ProductAvailabilitiesForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId),
            this.props.context.actionContext
        );

        const newShippingQuantity = newAvailableQuantity?.find(
            // @ts-expect-error
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (shipping: { deliveryType: any }) => shipping.deliveryType === DeliveryMode.shipping
        );
        const isCustomPriceSelected = productVariant.Dimensions?.find(
            dimension =>
                dimension.DimensionTypeValue === 4 && dimension.DimensionValue && dimension.DimensionValue.Value?.toLowerCase() === 'custom'
        );
        if (isCustomPriceSelected) {
            this.setState({ isCustomPriceSelected: true });
        } else {
            this.setState(previousState => {
                const errorState = previousState.errorState;
                errorState.customAmountError = undefined;
                return { isCustomPriceSelected: false, isPriceKeyedIn: false, errorState };
            });
        }

        if (newShippingQuantity) {
            this.setState({ productAvailableQuantity: newShippingQuantity });
        } else {
            this.setState({ productAvailableQuantity: undefined });
        }
    }

    private async _hydratePrice(productVariant: SimpleProduct): Promise<void> {
        // Step 6. Use these dimensions hydrate the product price.
        const newPrice = await getPriceForSelectedVariant(
            new PriceForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId),
            this.props.context.actionContext
        );

        if (newPrice) {
            this._updatePrice(newPrice);
        }
    }

    private async _hydrateDeliveryOptions(productVariant: SimpleProduct): Promise<void> {
        const retailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );

        // Step 7. Use these dimensions hydrate the product delivery options.
        const newDeliveryOptions = await getDeliveryOptionsForSelectedVariant(
            new GetDeliveryOptionsForSelectedVariantInput(
                productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                undefined,
                undefined,
                retailMultiplePickupFeatureState?.IsEnabled
            ),
            this.props.context.actionContext
        );

        if (newDeliveryOptions) {
            this.setState({ productDeliveryOptions: newDeliveryOptions });
        }
    }

    private async _updateQuantitiesInState(product: SimpleProduct): Promise<void> {
        const isOrderQuantityLimitsFeatureEnabled = await this._isOrderQuantityLimitsFeatureEnabled();
        if (isOrderQuantityLimitsFeatureEnabled && product) {
            this.setState({
                min: product.Behavior?.MinimumQuantity || 1,

                // If max by feature in default order settings is not defined then use max from site settings or default max 10.
                max:
                    product.Behavior?.MaximumQuantity && product.Behavior?.MaximumQuantity > 0
                        ? product.Behavior?.MaximumQuantity
                        : updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem)
            });
        } else {
            this.setState({
                min: 1,
                max: updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem)
            });
        }
    }

    private readonly _getDropdownName = (dimensionType: number, resources: IBuyboxResources): string => {
        const isGiftCard = this.props.data.product.result?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    private async _isOrderQuantityLimitsFeatureEnabled(): Promise<boolean> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        const featureStatuses = await this.props.data.featureState;
        const isFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
        )?.IsEnabled;
        if (!isFeatureEnabledInHq) {
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        let customerInfo;
        try {
            customerInfo = await this.props.data.customerInformation;
        } catch (error) {
            //@ts-ignore
            this.props.telemetry.information(error);
            this.props.telemetry.debug('Unable to receive Customer Information. May be user is not authorized');
            return false;
        }

        return (
            customerInfo &&
            ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
        );
    }
}

export default Buybox;
